import React, { useState, useEffect, useCallback, useRef, Suspense } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MapPin, Calculator, Users, ArrowRight, Play, Search, Star, X, Check, TrendingUp, Clock, ArrowDownAZ, Tag, ChevronDown, ChevronUp, ExternalLink, Heart, Award, Zap, Shield, SlidersHorizontal, DollarSign, Crown, Globe } from 'lucide-react';
import Navbar from '../components/Navbar';
import FreelancerApplicationModal from '../components/FreelancerApplicationModal';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Lazy load 3D Scene
const HeroScene = React.lazy(() => import('../components/canvas/HeroScene'));

// Use environment variable or fallback to production backend
const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://revocut-production.up.railway.app';

// Custom marker icons
const defaultIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

const boostedIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-violet.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

// Map Controller Component for programmatic map control
const MapController = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center && zoom) {
      map.flyTo(center, zoom, { duration: 0.8 });
    }
  }, [center, zoom, map]);
  return null;
};

// TeamMarker Component with auto-opening popup
const TeamMarker = ({ team, isSelected, onSelect, onViewProfile, onOpenCalculator, icon }) => {
  const markerRef = useRef(null);
  
  // Auto-open popup when this marker is selected
  useEffect(() => {
    if (isSelected && markerRef.current) {
      // Small delay to let the map fly to position first
      const timer = setTimeout(() => {
        markerRef.current.openPopup();
      }, 900);
      return () => clearTimeout(timer);
    }
  }, [isSelected]);
  
  return (
    <Marker
      ref={markerRef}
      position={[team.location.lat, team.location.lng]}
      icon={icon}
      eventHandlers={{ click: () => onSelect(team) }}
    >
      <Popup className="custom-popup">
        <div 
          className="p-4 rounded-xl min-w-[280px]"
          style={{ 
            backgroundColor: '#0a0a0a', 
            border: '1px solid rgba(92, 99, 255, 0.3)',
            margin: '-14px -20px -14px -20px'
          }}
        >
          {/* Header with Avatar */}
          <div className="flex items-start gap-3 mb-3">
            <div 
              className="w-14 h-14 rounded-xl flex items-center justify-center text-xl font-bold flex-shrink-0"
              style={{ backgroundColor: '#1a1a3e', color: '#5C63FF' }}
            >
              {team.name?.charAt(0) || 'F'}
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-white text-lg leading-tight">{team.name}</h4>
              <p className="text-gray-400 text-sm">
                {team.specializations?.[0] || 'Video Editor'}
              </p>
            </div>
          </div>
          
          {/* Rating & Location */}
          <div className="flex items-center gap-4 mb-4 text-sm">
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              <span className="text-yellow-400 font-medium">
                {team.rating?.toFixed(1) || '5.0'}
              </span>
            </div>
            <div className="flex items-center gap-1 text-gray-400">
              <MapPin className="w-4 h-4" />
              <span>{team.location?.city || 'Deutschland'}</span>
            </div>
            {team.is_premium && (
              <span className="px-2 py-0.5 text-xs font-bold rounded bg-purple-500/20 text-purple-400">
                PREMIUM
              </span>
            )}
          </div>
          
          {/* Buttons */}
          <div className="flex gap-2">
            <button 
              onClick={(e) => { e.stopPropagation(); onViewProfile(team); }}
              className="flex-1 py-2.5 px-4 rounded-xl text-sm font-medium transition-all text-white"
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
            >
              Profil ansehen
            </button>
            <button 
              onClick={(e) => { e.stopPropagation(); onOpenCalculator(team); }}
              className="flex-1 py-2.5 px-4 rounded-xl text-sm font-medium transition-all text-white"
              style={{ backgroundColor: '#5C63FF' }}
            >
              Buchen
            </button>
          </div>
        </div>
      </Popup>
    </Marker>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showCalculator, setShowCalculator] = useState(false);
  const [previewTeam, setPreviewTeam] = useState(null); // For the expanded preview card
  const [showApplicationModal, setShowApplicationModal] = useState(false); // Freelancer application modal
  
  // Map control state
  const [mapCenter, setMapCenter] = useState([51.1657, 10.4515]);
  const [mapZoom, setMapZoom] = useState(6);
  const [selectedTeamId, setSelectedTeamId] = useState(null); // For auto-opening popup
  
  // Enhanced search states
  const [sortBy, setSortBy] = useState('popular'); // popular, newest, alphabetical, price_low, price_high
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagCategories, setTagCategories] = useState([]);
  const [popularTags, setPopularTags] = useState([]);
  
  // Advanced filter states
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filterSellerLevel, setFilterSellerLevel] = useState([]); // [0, 1, 2, 3]
  const [filterPriceMin, setFilterPriceMin] = useState('');
  const [filterPriceMax, setFilterPriceMax] = useState('');
  const [filterMinRating, setFilterMinRating] = useState(0); // 0 = alle, 1-5 = Mindestbewertung
  const [filterPremium, setFilterPremium] = useState(false);
  
  // Favorites state
  const [favorites, setFavorites] = useState([]);
  const customerUser = JSON.parse(localStorage.getItem('customer_user') || 'null');

  const defaultMapCenter = [51.1657, 10.4515];

  // City/Country search with radius
  const searchLocations = [
    // Länder
    { name: 'Deutschland', lat: 51.1657, lng: 10.4515, type: 'country' },
    { name: 'Österreich', lat: 47.5162, lng: 14.5501, type: 'country' },
    { name: 'Schweiz', lat: 46.8182, lng: 8.2275, type: 'country' },
    { name: 'Niederlande', lat: 52.1326, lng: 5.2913, type: 'country' },
    { name: 'Belgien', lat: 50.5039, lng: 4.4699, type: 'country' },
    { name: 'Frankreich', lat: 46.2276, lng: 2.2137, type: 'country' },
    { name: 'Italien', lat: 41.8719, lng: 12.5674, type: 'country' },
    { name: 'Spanien', lat: 40.4637, lng: -3.7492, type: 'country' },
    { name: 'Polen', lat: 51.9194, lng: 19.1451, type: 'country' },
    { name: 'Tschechien', lat: 49.8175, lng: 15.4730, type: 'country' },
    { name: 'Dänemark', lat: 56.2639, lng: 9.5018, type: 'country' },
    { name: 'Schweden', lat: 60.1282, lng: 18.6435, type: 'country' },
    { name: 'Großbritannien', lat: 55.3781, lng: -3.4360, type: 'country' },
    // Deutsche Städte
    { name: 'Hamburg', lat: 53.5511, lng: 9.9937, type: 'city' },
    { name: 'Berlin', lat: 52.5200, lng: 13.4050, type: 'city' },
    { name: 'München', lat: 48.1351, lng: 11.5820, type: 'city' },
    { name: 'Köln', lat: 50.9375, lng: 6.9603, type: 'city' },
    { name: 'Frankfurt', lat: 50.1109, lng: 8.6821, type: 'city' },
    { name: 'Stuttgart', lat: 48.7758, lng: 9.1829, type: 'city' },
    { name: 'Düsseldorf', lat: 51.2277, lng: 6.7735, type: 'city' },
    { name: 'Leipzig', lat: 51.3397, lng: 12.3731, type: 'city' },
    { name: 'Dortmund', lat: 51.5136, lng: 7.4653, type: 'city' },
    { name: 'Essen', lat: 51.4556, lng: 7.0116, type: 'city' },
    { name: 'Bremen', lat: 53.0793, lng: 8.8017, type: 'city' },
    { name: 'Dresden', lat: 51.0504, lng: 13.7373, type: 'city' },
    { name: 'Hannover', lat: 52.3759, lng: 9.7320, type: 'city' },
    { name: 'Nürnberg', lat: 49.4521, lng: 11.0767, type: 'city' },
    { name: 'Duisburg', lat: 51.4344, lng: 6.7624, type: 'city' },
    { name: 'Bochum', lat: 51.4818, lng: 7.2162, type: 'city' },
    { name: 'Wuppertal', lat: 51.2562, lng: 7.1508, type: 'city' },
    { name: 'Bielefeld', lat: 52.0302, lng: 8.5325, type: 'city' },
    { name: 'Bonn', lat: 50.7374, lng: 7.0982, type: 'city' },
    { name: 'Mannheim', lat: 49.4875, lng: 8.4660, type: 'city' },
    { name: 'Karlsruhe', lat: 49.0069, lng: 8.4037, type: 'city' },
    { name: 'Augsburg', lat: 48.3705, lng: 10.8978, type: 'city' },
    { name: 'Wiesbaden', lat: 50.0782, lng: 8.2398, type: 'city' },
    { name: 'Kiel', lat: 54.3233, lng: 10.1228, type: 'city' },
    { name: 'Rostock', lat: 54.0924, lng: 12.0991, type: 'city' },
    // Österreichische Städte
    { name: 'Wien', lat: 48.2082, lng: 16.3738, type: 'city' },
    { name: 'Salzburg', lat: 47.8095, lng: 13.0550, type: 'city' },
    { name: 'Graz', lat: 47.0707, lng: 15.4395, type: 'city' },
    { name: 'Innsbruck', lat: 47.2692, lng: 11.4041, type: 'city' },
    // Schweizer Städte
    { name: 'Zürich', lat: 47.3769, lng: 8.5417, type: 'city' },
    { name: 'Bern', lat: 46.9480, lng: 7.4474, type: 'city' },
    { name: 'Basel', lat: 47.5596, lng: 7.5886, type: 'city' },
    { name: 'Genf', lat: 46.2044, lng: 6.1432, type: 'city' },
  ];
  const [selectedCity, setSelectedCity] = useState(null);
  const [searchRadius, setSearchRadius] = useState(50);
  const [citySearchQuery, setCitySearchQuery] = useState('');
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const citySearchRef = useRef(null);


  // Handle scrollTo from navigation state (e.g. from Services page)
  useEffect(() => {
    if (location.state?.scrollTo) {
      const el = document.getElementById(location.state.scrollTo);
      if (el) {
        el.scrollIntoView({ behavior: 'instant' });
      } else {
        const timer = setTimeout(() => {
          document.getElementById(location.state.scrollTo)?.scrollIntoView({ behavior: 'instant' });
        }, 500);
        return () => clearTimeout(timer);
      }
      // Clear the state so it doesn't re-scroll on re-renders
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  // Fetch teams
  useEffect(() => {
    const fetchTeams = async () => {
      try {
        console.log('Fetching teams from:', API_URL || 'relative URL');
        const response = await fetch(`${API_URL}/api/teams`);
        console.log('Response status:', response.status);
        if (response.ok) {
          const data = await response.json();
          console.log('Teams loaded:', data.length);
          setTeams(data);
        } else {
          console.error('Failed to fetch teams:', response.status);
        }
      } catch (error) {
        console.error('Error fetching teams:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTeams();
  }, []);

  // Fetch tag categories
  useEffect(() => {
    const fetchTagData = async () => {
      try {
        const [catRes, popRes] = await Promise.all([
          fetch(`${API_URL}/api/explore/tag-categories`),
          fetch(`${API_URL}/api/explore/popular-tags`)
        ]);
        
        if (catRes.ok) {
          const categories = await catRes.json();
          setTagCategories(categories);
        }
        
        if (popRes.ok) {
          const tags = await popRes.json();
          setPopularTags(tags);
        }
      } catch (error) {
        console.error('Error fetching tag data:', error);
      }
    };
    
    fetchTagData();
  }, []);

  // Fetch favorites for logged-in customer
  useEffect(() => {
    const fetchFavorites = async () => {
      if (!customerUser?.id) return;
      try {
        const res = await fetch(`${API_URL}/api/customer/${customerUser.id}/favorites`);
        if (res.ok) {
          const data = await res.json();
          setFavorites(data.favorites || []);
        }
      } catch (error) {
        console.error('Error fetching favorites:', error);
      }
    };
    fetchFavorites();
  }, [customerUser?.id]);

  // Toggle favorite
  const toggleFavorite = async (teamId, e) => {
    e?.stopPropagation();
    
    if (!customerUser?.id) {
      // Redirect to login if not logged in
      navigate('/login');
      return;
    }

    try {
      const isFavorite = favorites.includes(teamId);
      const res = await fetch(`${API_URL}/api/customer/${customerUser.id}/favorites`, {
        method: isFavorite ? 'DELETE' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ freelancer_id: teamId })
      });
      
      if (res.ok) {
        setFavorites(prev => 
          isFavorite 
            ? prev.filter(id => id !== teamId) 
            : [...prev, teamId]
        );
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  // Get seller level based on total revenue (Gesamtumsatz in €)
  const getSellerLevel = (totalRevenue) => {
    if (totalRevenue >= 100000) return { level: 10, label: 'Level 10', color: '#EC4899' };
    if (totalRevenue >= 75000) return { level: 9, label: 'Level 9', color: '#DC2626' };
    if (totalRevenue >= 50000) return { level: 8, label: 'Level 8', color: '#DC2626' };
    if (totalRevenue >= 35000) return { level: 7, label: 'Level 7', color: '#F97316' };
    if (totalRevenue >= 25000) return { level: 6, label: 'Level 6', color: '#F97316' };
    if (totalRevenue >= 15000) return { level: 5, label: 'Level 5', color: '#2563EB' };
    if (totalRevenue >= 10000) return { level: 4, label: 'Level 4', color: '#2563EB' };
    if (totalRevenue >= 5000) return { level: 3, label: 'Level 3', color: '#14B8A6' };
    if (totalRevenue >= 500) return { level: 2, label: 'Level 2', color: '#14B8A6' };
    return { level: 1, label: 'Level 1', color: '#16A34A' };
  };

  // Check if freelancer is top rated (4.8+ with 5+ reviews)
  const isTopRated = (rating) => {
    return rating >= 4.5;
  };

  // Haversine distance calculation (km)
  const getDistanceKm = (lat1, lng1, lat2, lng2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  };

  // Close city dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (citySearchRef.current && !citySearchRef.current.contains(e.target)) {
        setShowCityDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter teams by search query, tags, and advanced filters
  const filteredTeams = teams.filter(team => {
    // Search filter
    const matchesSearch = !searchQuery || 
      team.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      team.location?.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      team.specialization?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Tags filter - check team's specialization_tags or specialization
    const matchesTags = selectedTags.length === 0 || selectedTags.some(tag => 
      team.specialization_tags?.includes(tag) ||
      team.specialization?.toLowerCase().includes(tag.toLowerCase()) ||
      team.specializations?.some(s => s.toLowerCase().includes(tag.toLowerCase()))
    );
    
    // Seller Level filter
    const teamLevel = getSellerLevel(team.total_revenue || 0);
    const matchesLevel = filterSellerLevel.length === 0 || filterSellerLevel.includes(teamLevel.level);
    
    // Price filter (using base_price or hourly_rate)
    const teamPrice = team.pricing?.base_price || team.hourly_rate || 50;
    const matchesPriceMin = !filterPriceMin || teamPrice >= parseFloat(filterPriceMin);
    const matchesPriceMax = !filterPriceMax || teamPrice <= parseFloat(filterPriceMax);
    
    // Rating filter (minimum stars)
    const teamRating = team.rating || 5;
    const matchesRating = filterMinRating === 0 || teamRating >= filterMinRating;
    
    // Premium filter
    const matchesPremium = !filterPremium || team.is_premium;

    // City radius filter
    const matchesRadius = !selectedCity || (
      team.location?.lat && team.location?.lng &&
      getDistanceKm(selectedCity.lat, selectedCity.lng, team.location.lat, team.location.lng) <= searchRadius
    );
    
    return matchesSearch && matchesTags && matchesLevel && matchesPriceMin && matchesPriceMax && matchesRating && matchesPremium && matchesRadius;
  });

  // Sort teams based on sortBy
  const sortedTeams = [...filteredTeams].sort((a, b) => {
    const aIsRevoCut = a.name === 'RevoCut' || a.is_official;
    const bIsRevoCut = b.name === 'RevoCut' || b.is_official;
    
    // RevoCut always first
    if (aIsRevoCut && !bIsRevoCut) return -1;
    if (!aIsRevoCut && bIsRevoCut) return 1;
    
    // Then premium members
    if (a.is_premium && !b.is_premium) return -1;
    if (!a.is_premium && b.is_premium) return 1;
    
    // Then by sort option
    if (sortBy === 'popular') {
      return (b.rating || 5) - (a.rating || 5);
    } else if (sortBy === 'newest') {
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    } else if (sortBy === 'alphabetical') {
      return (a.name || '').localeCompare(b.name || '');
    } else if (sortBy === 'price_low') {
      const aPrice = a.pricing?.base_price || a.hourly_rate || 50;
      const bPrice = b.pricing?.base_price || b.hourly_rate || 50;
      return aPrice - bPrice;
    } else if (sortBy === 'price_high') {
      const aPrice = a.pricing?.base_price || a.hourly_rate || 50;
      const bPrice = b.pricing?.base_price || b.hourly_rate || 50;
      return bPrice - aPrice;
    }
    
    return 0;
  });

  // Toggle tag selection
  const toggleTag = (tag) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag) 
        : [...prev, tag]
    );
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedTags([]);
    setSearchQuery('');
    setFilterSellerLevel([]);
    setFilterPriceMin('');
    setFilterPriceMax('');
    setFilterMinRating(0);
    setFilterPremium(false);
    setSortBy('popular');
    setSelectedCity(null);
    setCitySearchQuery('');
    setSearchRadius(50);
  };
  
  // Check if any advanced filters are active
  const hasActiveFilters = selectedTags.length > 0 || filterSellerLevel.length > 0 || 
    filterPriceMin || filterPriceMax || filterMinRating > 0 || filterPremium || sortBy !== 'popular' || selectedCity;
  
  // Toggle seller level filter
  const toggleSellerLevel = (level) => {
    setFilterSellerLevel(prev => 
      prev.includes(level) 
        ? prev.filter(l => l !== level) 
        : [...prev, level]
    );
  };

  const handleSelectTeam = (team) => {
    // Toggle: if same team clicked again, close it
    if (previewTeam?.id === team.id) {
      setPreviewTeam(null);
      setSelectedTeamId(null);
      return;
    }
    setPreviewTeam(team);
    // Jump map to this team's location and open popup
    if (team.location?.lat && team.location?.lng) {
      setMapCenter([team.location.lat, team.location.lng]);
      setMapZoom(12);
      setSelectedTeamId(team.id); // This will trigger popup to open
    }
  };

  const handleOpenCalculator = (team) => {
    // Navigate to calculator with team ID (not freelancer_id!)
    navigate(`/calculator/${team.id}`);
  };

  const handleViewProfile = (team) => {
    // Navigate to freelancer profile
    navigate(`/freelancer/${team.id}/profile`);
  };

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: '#0a0a0a' }}>
      {/* Navigation */}
      <Navbar />

      {/* Hero Section - Clean & Minimal */}
      <section className="relative h-screen overflow-hidden">
        {/* Desktop Layout - Text links, 3D rechts */}
        <div className="hidden lg:flex relative z-10 h-full items-center">
          {/* Left side - Text */}
          <div className="w-1/2 pl-16 xl:pl-24">
            <h1 
              className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Revolution of{' '}
              <span style={{ color: '#5C63FF' }}>Multimedia</span>
            </h1>

            <p className="text-lg md:text-xl text-gray-400 mb-10 max-w-xl leading-relaxed">
              Finde professionelle Freelancer für Post-Production. 
              Video-Editing, Motion Design, Color Grading – transparent kalkulieren.
            </p>

            <div className="flex flex-wrap gap-4 relative z-20">
              <a 
                href="#map"
                className="px-8 py-4 rounded-full text-base font-semibold transition-all hover:scale-105 flex items-center gap-2"
                style={{ backgroundColor: '#5C63FF' }}
              >
                <MapPin className="w-5 h-5" />
                Freelancer finden
              </a>
              <button 
                onClick={() => navigate('/team')}
                className="px-8 py-4 rounded-full text-base font-semibold border border-white/20 hover:border-white/40 transition-all flex items-center gap-2"
              >
                <Users className="w-5 h-5" />
                Über uns
              </button>
            </div>
          </div>
          
          {/* Right side - 3D Scene - pointer-events-none so buttons are clickable */}
          <div className="w-1/2 h-full pointer-events-none">
            <Suspense fallback={
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-center">
                  <div className="text-4xl mb-2 animate-bounce">🎬</div>
                  <p className="text-gray-500 text-xs">Lade 3D...</p>
                </div>
              </div>
            }>
              <HeroScene className="w-full h-full" />
            </Suspense>
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="lg:hidden flex flex-col h-full pt-24 px-6">
          <div className="flex-1 flex flex-col justify-center">
            <h1 
              className="text-4xl sm:text-5xl font-bold leading-tight mb-6"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Revolution of{' '}
              <span style={{ color: '#5C63FF' }}>Multimedia</span>
            </h1>

            <p className="text-base text-gray-400 mb-8 leading-relaxed">
              Finde professionelle Freelancer für Post-Production.
            </p>

            <div className="flex flex-wrap gap-4">
              <a 
                href="#map"
                className="px-6 py-3 rounded-full text-sm font-semibold transition-all flex items-center gap-2"
                style={{ backgroundColor: '#5C63FF' }}
              >
                <MapPin className="w-4 h-4" />
                Freelancer finden
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section id="map" className="px-6 lg:px-12 py-20">
        <div className="mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Freelancer in deiner <span style={{ color: '#5C63FF' }}>Nähe</span>
          </h2>
          <p className="text-gray-400 text-lg">
            Wähle einen Freelancer aus, um dein Projekt zu kalkulieren.
          </p>
        </div>

        {/* Unified Filter Bar - Mobile Optimized */}
        <div className="mb-8">
          {/* Main Filter Bar: Filter Button + Search */}
          <div className="flex items-center gap-3">
            {/* Filter Button */}
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className={`px-4 py-3 rounded-xl flex items-center gap-2 transition-all font-medium shrink-0 ${
                showAdvancedFilters || hasActiveFilters
                  ? 'text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
              style={{ 
                backgroundColor: showAdvancedFilters || hasActiveFilters
                  ? '#5C63FF' 
                  : 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
              data-testid="filter-btn"
            >
              <SlidersHorizontal className="w-5 h-5" />
              <span className="hidden sm:inline">Filter</span>
              {hasActiveFilters && (
                <span className="w-5 h-5 rounded-full text-xs flex items-center justify-center font-bold bg-white text-[#5C63FF]">
                  {selectedTags.length + filterSellerLevel.length + (filterMinRating > 0 ? 1 : 0) + (filterPremium ? 1 : 0) + (filterPriceMin || filterPriceMax ? 1 : 0) + (sortBy !== 'popular' ? 1 : 0)}
                </span>
              )}
            </button>

            {/* Search Input */}
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                placeholder="Nach Name oder Skill suchen..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full py-3 pl-12 pr-4 rounded-xl border border-white/10 bg-white/5 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 transition-colors"
                data-testid="search-input"
              />
            </div>
          </div>

          {/* Active Filters Summary (compact chips) */}
          {hasActiveFilters && (
            <div className="flex items-center gap-2 mt-3 flex-wrap" data-testid="active-filters">
              {selectedTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => toggleTag(tag)}
                  className="px-2.5 py-1 rounded-full text-xs flex items-center gap-1.5 transition-all"
                  style={{ backgroundColor: 'rgba(92, 99, 255, 0.2)', color: '#5C63FF' }}
                >
                  #{tag}
                  <X className="w-3 h-3" />
                </button>
              ))}
              {filterSellerLevel.map(level => (
                <button
                  key={`level-${level}`}
                  onClick={() => toggleSellerLevel(level)}
                  className="px-2.5 py-1 rounded-full text-xs flex items-center gap-1.5 bg-green-500/20 text-green-400"
                >
                  {level === 0 ? 'Neu' : `Level ${level}`}
                  <X className="w-3 h-3" />
                </button>
              ))}
              {filterMinRating > 0 && (
                <button
                  onClick={() => setFilterMinRating(0)}
                  className="px-2.5 py-1 rounded-full text-xs flex items-center gap-1.5 bg-yellow-500/20 text-yellow-400"
                >
                  {filterMinRating}+ Sterne
                  <X className="w-3 h-3" />
                </button>
              )}
              {filterPremium && (
                <button
                  onClick={() => setFilterPremium(false)}
                  className="px-2.5 py-1 rounded-full text-xs flex items-center gap-1.5 bg-purple-500/20 text-purple-400"
                >
                  Premium
                  <X className="w-3 h-3" />
                </button>
              )}
              {(filterPriceMin || filterPriceMax) && (
                <button
                  onClick={() => { setFilterPriceMin(''); setFilterPriceMax(''); }}
                  className="px-2.5 py-1 rounded-full text-xs flex items-center gap-1.5 bg-blue-500/20 text-blue-400"
                >
                  €{filterPriceMin || '0'}-{filterPriceMax || '∞'}
                  <X className="w-3 h-3" />
                </button>
              )}
              {sortBy !== 'popular' && (
                <button
                  onClick={() => setSortBy('popular')}
                  className="px-2.5 py-1 rounded-full text-xs flex items-center gap-1.5 bg-white/10 text-gray-300"
                >
                  {sortBy === 'newest' ? 'Neueste' : sortBy === 'alphabetical' ? 'A-Z' : sortBy === 'price_low' ? 'Preis ↑' : 'Preis ↓'}
                  <X className="w-3 h-3" />
                </button>
              )}
              <button
                onClick={clearFilters}
                className="text-gray-500 hover:text-white text-xs transition-colors ml-1"
              >
                Alle löschen
              </button>
            </div>
          )}

          {/* Unified Filter Panel - Contains EVERYTHING */}
          {showAdvancedFilters && (
            <div 
              className="mt-4 rounded-2xl border border-white/10 overflow-hidden animate-in slide-in-from-top-2 duration-200 flex flex-col"
              style={{ backgroundColor: 'rgba(10, 10, 10, 0.95)', maxHeight: 'calc(100vh - 200px)' }}
              data-testid="filter-panel"
            >
              {/* Mobile-friendly scrollable content */}
              <div className="flex-1 overflow-y-auto p-5 space-y-6">
                
                {/* SECTION 1: Sortierung */}
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <ArrowDownAZ className="w-4 h-4" />
                    Sortierung
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { id: 'popular', label: 'Beliebt', icon: TrendingUp },
                      { id: 'newest', label: 'Neueste', icon: Clock },
                      { id: 'alphabetical', label: 'A-Z', icon: ArrowDownAZ },
                      { id: 'price_low', label: 'Preis ↑', icon: DollarSign },
                      { id: 'price_high', label: 'Preis ↓', icon: DollarSign }
                    ].map(({ id, label, icon: Icon }) => (
                      <button
                        key={id}
                        onClick={() => setSortBy(id)}
                        className={`px-3 py-2 rounded-lg text-sm transition-all flex items-center gap-2 ${
                          sortBy === id ? 'ring-2 ring-[#5C63FF]' : ''
                        }`}
                        style={{ 
                          backgroundColor: sortBy === id 
                            ? 'rgba(92, 99, 255, 0.2)' 
                            : 'rgba(255, 255, 255, 0.05)',
                          color: sortBy === id ? '#5C63FF' : '#9CA3AF'
                        }}
                      >
                        <Icon className="w-4 h-4" />
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* SECTION 2: Tags */}
                <div className="border-t border-white/5 pt-5">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Tag className="w-4 h-4" />
                    Tags & Spezialisierungen
                  </p>
                  
                  {/* Popular Tags */}
                  {popularTags.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs text-gray-600 mb-2">Beliebte Tags</p>
                      <div className="flex flex-wrap gap-2">
                        {popularTags.slice(0, 12).map(tag => (
                          <button
                            key={tag}
                            onClick={() => toggleTag(tag)}
                            className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                              selectedTags.includes(tag)
                                ? 'text-white ring-2 ring-[#5C63FF]'
                                : 'text-gray-400 hover:text-white'
                            }`}
                            style={{ 
                              backgroundColor: selectedTags.includes(tag) 
                                ? 'rgba(92, 99, 255, 0.3)' 
                                : 'rgba(255, 255, 255, 0.05)'
                            }}
                          >
                            #{tag}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Tag Categories */}
                  {tagCategories.map(category => (
                    <div key={category.id} className="mb-4">
                      <p className="text-xs text-gray-600 mb-2">{category.name}</p>
                      <div className="flex flex-wrap gap-2">
                        {category.tags?.slice(0, 8).map(tag => (
                          <button
                            key={tag}
                            onClick={() => toggleTag(tag)}
                            className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                              selectedTags.includes(tag)
                                ? 'text-white ring-2 ring-[#5C63FF]'
                                : 'text-gray-400 hover:text-white'
                            }`}
                            style={{ 
                              backgroundColor: selectedTags.includes(tag) 
                                ? 'rgba(92, 99, 255, 0.3)' 
                                : 'rgba(255, 255, 255, 0.05)'
                            }}
                          >
                            #{tag}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* SECTION 3: Seller Level (10 Levels) */}
                <div className="border-t border-white/5 pt-5">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Award className="w-4 h-4" />
                    Seller Level
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { level: 0, label: 'Neu', color: '#6B7280' },
                      { level: 1, label: 'Level 1', color: '#22C55E' },
                      { level: 2, label: 'Level 2', color: '#10B981' },
                      { level: 3, label: 'Level 3', color: '#06B6D4' },
                      { level: 4, label: 'Level 4', color: '#3B82F6' },
                      { level: 5, label: 'Level 5', color: '#6366F1' },
                      { level: 6, label: 'Level 6', color: '#8B5CF6' },
                      { level: 7, label: 'Level 7', color: '#EC4899' },
                      { level: 8, label: 'Level 8', color: '#EF4444' },
                      { level: 9, label: 'Level 9', color: '#F59E0B' },
                      { level: 10, label: 'Level 10', color: '#FFD700' }
                    ].map(({ level, label, color }) => (
                      <button
                        key={level}
                        onClick={() => toggleSellerLevel(level)}
                        className={`px-3 py-1.5 rounded-lg text-xs transition-all flex items-center gap-1 ${
                          filterSellerLevel.includes(level) ? 'ring-2' : ''
                        }`}
                        style={{ 
                          backgroundColor: filterSellerLevel.includes(level) 
                            ? `${color}30` 
                            : 'rgba(255, 255, 255, 0.05)',
                          color: filterSellerLevel.includes(level) ? color : '#9CA3AF',
                          ringColor: color
                        }}
                      >
                        {level >= 8 && <Award className="w-3 h-3" />}
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* SECTION 4: Price & Special Filters (2 columns on desktop) */}
                <div className="border-t border-white/5 pt-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {/* Price Range */}
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <DollarSign className="w-4 h-4" />
                      Preis (€/Min)
                    </p>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        placeholder="Min"
                        value={filterPriceMin}
                        onChange={(e) => setFilterPriceMin(e.target.value)}
                        className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm focus:outline-none focus:border-purple-500"
                        data-testid="price-min-input"
                      />
                      <span className="text-gray-500">-</span>
                      <input
                        type="number"
                        placeholder="Max"
                        value={filterPriceMax}
                        onChange={(e) => setFilterPriceMax(e.target.value)}
                        className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm focus:outline-none focus:border-purple-500"
                        data-testid="price-max-input"
                      />
                    </div>
                  </div>

                  {/* Rating Filter */}
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <Star className="w-4 h-4" />
                      Mindestbewertung
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {[0, 3, 3.5, 4, 4.5, 5].map((rating) => (
                        <button
                          key={rating}
                          onClick={() => setFilterMinRating(rating)}
                          className={`px-3 py-2 rounded-lg text-sm transition-all flex items-center gap-1.5 ${
                            filterMinRating === rating ? 'ring-2 ring-yellow-500' : ''
                          }`}
                          style={{ 
                            backgroundColor: filterMinRating === rating 
                              ? 'rgba(250, 204, 21, 0.2)' 
                              : 'rgba(255, 255, 255, 0.05)',
                            color: filterMinRating === rating ? '#FACC15' : '#9CA3AF'
                          }}
                          data-testid={`rating-filter-${rating}`}
                        >
                          {rating === 0 ? (
                            'Alle'
                          ) : (
                            <>
                              <Star className="w-3.5 h-3.5 fill-current" />
                              {rating}+
                            </>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Premium Filter */}
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <Crown className="w-4 h-4" />
                      Besondere Filter
                    </p>
                    <button
                      onClick={() => setFilterPremium(!filterPremium)}
                      className={`px-3 py-2.5 rounded-lg text-sm transition-all flex items-center gap-2 ${
                        filterPremium ? 'ring-2 ring-purple-500' : ''
                      }`}
                      style={{ 
                        backgroundColor: filterPremium 
                          ? 'rgba(139, 92, 246, 0.2)' 
                          : 'rgba(255, 255, 255, 0.05)',
                        color: filterPremium ? '#8B5CF6' : '#9CA3AF'
                      }}
                      data-testid="premium-filter"
                    >
                      <Crown className="w-4 h-4" />
                      Premium Member
                      {filterPremium && <Check className="w-3 h-3 ml-auto" />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Sticky Footer with Results & Actions */}
              <div className="shrink-0 p-4 border-t border-white/10 bg-[#0a0a0a] flex items-center justify-between gap-4">
                <p className="text-sm text-gray-400">
                  <span className="font-semibold text-white">{sortedTeams.length}</span> Freelancer gefunden
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2.5 rounded-lg text-sm bg-white/5 text-gray-400 hover:text-white hover:bg-white/10 transition-all"
                    data-testid="reset-filters-btn"
                  >
                    Zurücksetzen
                  </button>
                  <button
                    onClick={() => setShowAdvancedFilters(false)}
                    className="px-5 py-2.5 rounded-lg text-sm font-medium transition-all hover:opacity-90"
                    style={{ backgroundColor: '#5C63FF', color: 'white' }}
                    data-testid="apply-filters-btn"
                  >
                    Anwenden
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Map + List Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Map Container */}
          <div className="lg:col-span-2">
            {/* City + Radius Search */}
            <div className="flex items-center gap-3 mb-3" data-testid="city-radius-search">
              <div className="relative flex-1" ref={citySearchRef}>
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  data-testid="city-search-input"
                  type="text"
                  placeholder="Stadt oder Land suchen..."
                  value={citySearchQuery}
                  onChange={(e) => {
                    setCitySearchQuery(e.target.value);
                    setShowCityDropdown(true);
                    if (!e.target.value) {
                      setSelectedCity(null);
                    }
                  }}
                  onFocus={() => setShowCityDropdown(true)}
                  className="w-full pl-10 pr-8 py-2.5 rounded-xl text-sm bg-white/[0.05] border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-[#5C63FF]/50"
                />
                {selectedCity && (
                  <button
                    onClick={() => { setSelectedCity(null); setCitySearchQuery(''); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                {showCityDropdown && citySearchQuery && !selectedCity && (
                  <div className="absolute z-50 top-full mt-1 w-full rounded-xl bg-[#1a1a1a] border border-white/10 max-h-48 overflow-y-auto">
                    {(() => {
                      const filtered = searchLocations.filter(c => c.name.toLowerCase().includes(citySearchQuery.toLowerCase()));
                      const countries = filtered.filter(c => c.type === 'country');
                      const cities = filtered.filter(c => c.type === 'city');
                      return (
                        <>
                          {countries.length > 0 && (
                            <>
                              <div className="px-4 py-1.5 text-xs text-gray-500 uppercase tracking-wider">Länder</div>
                              {countries.map(loc => (
                                <button
                                  key={loc.name}
                                  data-testid={`city-option-${loc.name.toLowerCase()}`}
                                  onClick={() => {
                                    setSelectedCity(loc);
                                    setCitySearchQuery(loc.name);
                                    setShowCityDropdown(false);
                                    setMapCenter([loc.lat, loc.lng]);
                                    setMapZoom(loc.type === 'country' ? 6 : 9);
                                    if (loc.type === 'country') setSearchRadius(200);
                                  }}
                                  className="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:bg-white/10 hover:text-white flex items-center gap-2"
                                >
                                  <Globe className="w-3.5 h-3.5 text-blue-400" />
                                  {loc.name}
                                </button>
                              ))}
                            </>
                          )}
                          {cities.length > 0 && (
                            <>
                              <div className="px-4 py-1.5 text-xs text-gray-500 uppercase tracking-wider">Städte</div>
                              {cities.map(loc => (
                                <button
                                  key={loc.name}
                                  data-testid={`city-option-${loc.name.toLowerCase()}`}
                                  onClick={() => {
                                    setSelectedCity(loc);
                                    setCitySearchQuery(loc.name);
                                    setShowCityDropdown(false);
                                    setMapCenter([loc.lat, loc.lng]);
                                    setMapZoom(9);
                                  }}
                                  className="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:bg-white/10 hover:text-white flex items-center gap-2"
                                >
                                  <MapPin className="w-3.5 h-3.5 text-gray-500" />
                                  {loc.name}
                                </button>
                              ))}
                            </>
                          )}
                          {filtered.length === 0 && (
                            <div className="px-4 py-3 text-sm text-gray-500">Keine Ergebnisse gefunden</div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <input
                  data-testid="radius-slider"
                  type="range"
                  min="10"
                  max="500"
                  step="10"
                  value={searchRadius}
                  onChange={(e) => setSearchRadius(parseInt(e.target.value))}
                  className="w-28 accent-[#5C63FF]"
                />
                <span className="text-sm text-gray-400 w-16 text-right whitespace-nowrap" data-testid="radius-value">{searchRadius} km</span>
              </div>
            </div>

            <div 
              className="rounded-3xl overflow-hidden border border-white/10 h-[350px] md:h-[550px]"
            >
              <MapContainer
                center={defaultMapCenter}
                zoom={6}
                minZoom={3}
                maxZoom={12}
                maxBounds={[
                  [20, -30],  // Southwest corner (more space)
                  [75, 50]    // Northeast corner (more space)
                ]}
                maxBoundsViscosity={0.8}
                style={{ height: '100%', width: '100%', backgroundColor: '#0a0a0a' }}
                scrollWheelZoom={true}
                dragging={true}
              >
                <MapController center={mapCenter} zoom={mapZoom} />
                <TileLayer
                  attribution='&copy; CARTO'
                  url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                />
                {sortedTeams.map((team) => {
                  if (!team.location?.lat || !team.location?.lng) return null;
                  return (
                    <TeamMarker 
                      key={team.id}
                      team={team}
                      isSelected={selectedTeamId === team.id}
                      onSelect={handleSelectTeam}
                      onViewProfile={handleViewProfile}
                      onOpenCalculator={handleOpenCalculator}
                      icon={defaultIcon}
                    />
                  );
                })}
                {selectedCity && (
                  <Circle
                    center={[selectedCity.lat, selectedCity.lng]}
                    radius={searchRadius * 1000}
                    pathOptions={{
                      color: '#5C63FF',
                      fillColor: '#5C63FF',
                      fillOpacity: 0.08,
                      weight: 2,
                      dashArray: '6 4',
                    }}
                  />
                )}
              </MapContainer>
            </div>
          </div>

          {/* Team List */}
          <div className="lg:col-span-1">
            <div 
              className="rounded-3xl p-6 border border-white/10 bg-white/[0.02] overflow-y-auto"
              style={{ height: '550px' }}
            >
              <h3 className="text-lg font-bold mb-5" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {sortedTeams.length} Freelancer verfügbar
              </h3>
              
              {loading ? (
                <div className="flex items-center justify-center h-40">
                  <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : sortedTeams.length === 0 ? (
                <div className="text-center py-12">
                  <MapPin className="w-10 h-10 mx-auto mb-3 text-gray-600" />
                  <p className="text-gray-500">Keine Freelancer gefunden</p>
                  {selectedTags.length > 0 && (
                    <button
                      onClick={clearFilters}
                      className="mt-4 px-4 py-2 text-sm rounded-xl transition-all"
                      style={{ backgroundColor: '#5C63FF' }}
                    >
                      Filter zurücksetzen
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {sortedTeams.map((team) => {
                    const isRevoCut = team.name === 'RevoCut' || team.is_official;
                    const isPreview = previewTeam?.id === team.id;
                    const displayTags = team.specialization_tags || 
                      (team.specialization ? [team.specialization] : ['Video Editor']);
                    const isFavorited = favorites.includes(team.freelancer_id || team.id);
                    const sellerLevel = getSellerLevel(team.total_revenue || 0);
                    const topRated = isTopRated(team.rating || 5);
                    
                    return (
                      <div
                        key={team.id}
                        className={`rounded-2xl overflow-hidden transition-all ${
                          isRevoCut 
                            ? 'border-2 border-purple-500 bg-gradient-to-r from-purple-500/10 to-purple-600/5 shadow-lg shadow-purple-500/20' 
                            : isPreview
                                ? 'border border-purple-500/50 bg-white/[0.04]'
                                : 'border border-white/10 hover:border-purple-500/30 hover:bg-white/[0.03]'
                        }`}
                      >
                        {/* Collapsed View */}
                        <div
                          onClick={() => handleSelectTeam(team)}
                          className="p-4 cursor-pointer"
                        >
                          {/* Badges Row */}
                          <div className="flex items-center gap-2 mb-3 flex-wrap">
                            {isRevoCut && (
                              <span className="text-xs font-bold px-2 py-1 rounded-full bg-purple-500 text-white">
                                OFFIZIELL
                              </span>
                            )}
                            {topRated && (
                              <span className="text-xs font-bold px-2 py-1 rounded-full bg-orange-500/20 text-orange-400 border border-orange-500/40">
                                TOP RATED
                              </span>
                            )}
                            {sellerLevel.level >= 0 && (
                              <span 
                                className="text-xs font-bold px-2 py-1 rounded-full"
                                style={{ backgroundColor: `${sellerLevel.color}25`, color: sellerLevel.color, border: `1px solid ${sellerLevel.color}40` }}
                              >
                                {sellerLevel.label}
                              </span>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-4">
                            <div 
                              className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0`}
                              style={{ backgroundColor: isRevoCut ? '#5C63FF' : 'rgba(92, 99, 255, 0.15)' }}
                            >
                              <span className={`font-bold text-lg`} style={{ color: isRevoCut ? '#ffffff' : '#5C63FF' }}>
                                {team.name?.charAt(0)}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h4 className={`font-semibold truncate ${isRevoCut ? 'text-purple-300' : ''}`}>{team.name}</h4>
                                <div className="flex items-center gap-1 text-yellow-500">
                                  <Star className="w-3.5 h-3.5 fill-current" />
                                  <span className="text-sm font-medium">{(team.rating || 5).toFixed(1)}</span>
                                  {team.review_count > 0 && (
                                    <span className="text-xs text-gray-500">({team.review_count})</span>
                                  )}
                                </div>
                              </div>
                              <p className="text-sm text-gray-500">{team.specialization || 'Video Editor'}</p>
                            </div>
                            
                            {/* Favorite Button */}
                            <button
                              onClick={(e) => toggleFavorite(team.freelancer_id || team.id, e)}
                              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                              title={isFavorited ? 'Aus Favoriten entfernen' : 'Zu Favoriten hinzufügen'}
                            >
                              <Heart 
                                className={`w-5 h-5 transition-colors ${isFavorited ? 'fill-red-500 text-red-500' : 'text-gray-500 hover:text-red-400'}`}
                              />
                            </button>
                            
                            <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${isPreview ? 'rotate-180' : ''}`} />
                          </div>
                        </div>

                        {/* Expanded Preview */}
                        {isPreview && (
                          <div className="px-4 pb-4 border-t border-white/10 animate-in slide-in-from-top-2 duration-200">
                            <div className="pt-4">
                              {/* Rating & Location */}
                              <div className="flex items-center gap-4 mb-4">
                                <div className="flex items-center gap-1 text-yellow-500">
                                  <Star className="w-4 h-4 fill-current" />
                                  <span className="font-medium">{(team.rating || 5).toFixed(1)}</span>
                                  {team.review_count > 0 && (
                                    <span className="text-xs text-gray-400">({team.review_count} Bewertungen)</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-1 text-gray-400">
                                  <MapPin className="w-4 h-4" />
                                  <span>{team.location?.city || 'Deutschland'}</span>
                                </div>
                                {team.jobs_completed > 0 && (
                                  <div className="flex items-center gap-1 text-gray-400">
                                    <Check className="w-4 h-4" />
                                    <span>{team.jobs_completed} Jobs</span>
                                  </div>
                                )}
                              </div>

                              {/* Tags */}
                              <div className="flex flex-wrap gap-2 mb-4">
                                {displayTags.slice(0, 3).map(tag => (
                                  <span
                                    key={tag}
                                    className="px-3 py-1 rounded-lg text-sm"
                                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
                                  >
                                    #{tag}
                                  </span>
                                ))}
                              </div>

                              {/* Action Buttons */}
                              <div className="flex items-center gap-3 pt-3 border-t border-white/5">
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleViewProfile(team); }}
                                  className="flex-1 py-2.5 rounded-xl text-sm font-medium transition-all hover:bg-white/10"
                                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
                                >
                                  Profil ansehen
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleOpenCalculator(team); }}
                                  className="flex-1 py-2.5 rounded-xl text-sm font-medium transition-all hover:opacity-90"
                                  style={{ backgroundColor: '#5C63FF' }}
                                >
                                  Buchen
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* How it Works - Clean Cards */}
      <section className="px-6 lg:px-12 py-20">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            So funktioniert's
          </h2>
          <p className="text-gray-400 text-lg max-w-xl mx-auto">
            In drei einfachen Schritten zum perfekten Freelancer.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {[
            { icon: MapPin, title: 'Freelancer finden', desc: 'Durchsuche die Karte und finde Freelancer in deiner Nähe.' },
            { icon: Calculator, title: 'Kalkulieren', desc: 'Stelle dein Projekt zusammen mit transparenten Preisen.' },
            { icon: Users, title: 'Buchen', desc: 'Sende deine Anfrage und wir vermitteln dich.' },
          ].map((step, i) => (
            <div 
              key={i} 
              className="p-8 rounded-3xl border border-white/10 bg-white/[0.02] text-center transition-all hover:border-purple-500/30"
            >
              <div 
                className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6"
                style={{ backgroundColor: 'rgba(92, 99, 255, 0.1)' }}
              >
                <step.icon className="w-8 h-8" style={{ color: '#5C63FF' }} />
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {step.title}
              </h3>
              <p className="text-gray-400">{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Platform Features - Premium, Level System, etc. */}
      <section className="px-6 lg:px-12 py-20 border-t border-white/5">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Unsere Features
          </h2>
          <p className="text-gray-400 text-lg max-w-xl mx-auto">
            Eine Plattform, die für Freelancer und Kunden entwickelt wurde.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {/* Premium Membership */}
          <div 
            className="p-6 rounded-2xl border border-purple-500/20 bg-gradient-to-br from-purple-500/10 to-purple-600/5 transition-all hover:border-purple-500/40"
          >
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
              style={{ backgroundColor: 'rgba(139, 92, 246, 0.2)' }}
            >
              <Crown className="w-6 h-6 text-purple-400" />
            </div>
            <h3 className="text-lg font-bold mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Premium Membership
            </h3>
            <p className="text-gray-400 text-sm">
              Freelancer können Premium-Abos nutzen für mehr Sichtbarkeit, Analytics und exklusive Features.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-1">
              <span className="px-2 py-1 text-xs font-bold rounded bg-purple-500/20 text-purple-400">Basic</span>
              <span className="px-2 py-1 text-xs font-bold rounded bg-purple-500/30 text-purple-300">Pro</span>
              <span className="px-2 py-1 text-xs font-bold rounded bg-purple-500/40 text-purple-200">Elite</span>
            </div>
          </div>

          {/* 10 Level System */}
          <div 
            className="p-6 rounded-2xl border border-gradient-to-r from-green-500/20 to-yellow-500/20 bg-gradient-to-br from-green-500/5 to-yellow-500/5 transition-all hover:border-green-500/40"
            style={{ borderColor: 'rgba(16, 185, 129, 0.2)' }}
          >
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
              style={{ backgroundColor: 'rgba(16, 185, 129, 0.2)' }}
            >
              <TrendingUp className="w-6 h-6 text-green-400" />
            </div>
            <h3 className="text-lg font-bold mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              10 Seller-Level
            </h3>
            <p className="text-gray-400 text-sm">
              Steige durch 10 Level auf - basierend auf deinem Gesamtumsatz. Je höher, desto besser!
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-1">
              <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-green-500/20 text-green-400">1</span>
              <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-cyan-500/20 text-cyan-400">3</span>
              <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-blue-500/20 text-blue-400">5</span>
              <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-purple-500/20 text-purple-400">7</span>
              <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-yellow-500/20 text-yellow-400">10</span>
            </div>
          </div>

          {/* Rating System */}
          <div 
            className="p-6 rounded-2xl border border-yellow-500/20 bg-yellow-500/5 transition-all hover:border-yellow-500/40"
          >
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
              style={{ backgroundColor: 'rgba(250, 204, 21, 0.15)' }}
            >
              <Star className="w-6 h-6 text-yellow-400" />
            </div>
            <h3 className="text-lg font-bold mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Bewertungen
            </h3>
            <p className="text-gray-400 text-sm">
              Kunden bewerten Freelancer nach jedem Projekt. Finde Top-bewertete Profis.
            </p>
            <div className="mt-3 flex items-center gap-1">
              {[1,2,3,4,5].map(i => (
                <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
          </div>

          {/* Favoriten System */}
          <div 
            className="p-6 rounded-2xl border border-pink-500/20 bg-pink-500/5 transition-all hover:border-pink-500/40"
          >
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
              style={{ backgroundColor: 'rgba(236, 72, 153, 0.2)' }}
            >
              <Heart className="w-6 h-6 text-pink-400" />
            </div>
            <h3 className="text-lg font-bold mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Favoriten
            </h3>
            <p className="text-gray-400 text-sm">
              Speichere deine Lieblings-Freelancer und finde sie später schnell wieder.
            </p>
            <div className="mt-3 flex items-center gap-1 text-pink-400">
              <Heart className="w-4 h-4 fill-pink-400" />
              <span className="text-xs">Als Favorit speichern</span>
            </div>
          </div>
        </div>

        {/* For Freelancers CTA */}
        <div className="mt-12 text-center">
          <p className="text-gray-400 mb-4">
            Du bist Freelancer? Registriere dich und starte durch!
          </p>
          <button
            onClick={() => setShowApplicationModal(true)}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all hover:opacity-90"
            style={{ backgroundColor: 'rgba(92, 99, 255, 0.2)', color: '#5C63FF' }}
          >
            <Shield className="w-5 h-5" />
            Als Freelancer bewerben
          </button>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 lg:px-12 py-20">
        <div 
          className="rounded-3xl p-12 md:p-16 text-center border border-white/10"
          style={{ background: 'linear-gradient(135deg, rgba(92, 99, 255, 0.1) 0%, rgba(92, 99, 255, 0.02) 100%)' }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Bereit loszulegen?
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-lg mx-auto">
            Finde jetzt den perfekten Freelancer für dein nächstes Video-Projekt.
          </p>
          <a 
            href="#map"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-base font-semibold transition-all hover:scale-105"
            style={{ backgroundColor: '#5C63FF' }}
          >
            Jetzt starten
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 lg:px-12 py-10 border-t border-white/10">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-0.5">
            <img 
              src="/revocut-logo.png" 
              alt="RevoCut Logo" 
              className="w-8 h-8 object-contain"
            />
            <span className="font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>REVOCUT</span>
          </div>
          
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500">
            <button onClick={() => navigate('/impressum')} className="hover:text-white transition-colors">Impressum</button>
            <button onClick={() => navigate('/datenschutz')} className="hover:text-white transition-colors">Datenschutz</button>
            <button onClick={() => navigate('/agb')} className="hover:text-white transition-colors">AGB</button>
            <button onClick={() => navigate('/widerruf')} className="hover:text-white transition-colors">Widerruf</button>
            <button onClick={() => navigate('/cookies')} className="hover:text-white transition-colors">Cookies</button>
          </div>

          <p className="text-sm text-gray-600">© 2026 RevoCut Media</p>
        </div>
      </footer>

      {/* Calculator Modal */}
      {showCalculator && selectedTeam && (
        <CalculatorModal 
          team={selectedTeam} 
          onClose={() => { setShowCalculator(false); setSelectedTeam(null); }}
        />
      )}
    </div>
  );
};

// Advanced Calculator Modal - Multi-Step with Full Cost Calculation
const CalculatorModal = ({ team, onClose }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Video Type, 2: Details, 3: Addons, 4: Summary
  const [loading, setLoading] = useState(true);
  const [pricingConfig, setPricingConfig] = useState(null);
  const [freelancerPricing, setFreelancerPricing] = useState(null);
  
  // Form state
  const [videoType, setVideoType] = useState('');
  const [rawMaterialHours, setRawMaterialHours] = useState(1);
  const [outputMinutes, setOutputMinutes] = useState(1);
  const [videoCount, setVideoCount] = useState(1);
  const [selectedAddons, setSelectedAddons] = useState([]);
  
  // Calculation result
  const [calculation, setCalculation] = useState(null);
  const [calculating, setCalculating] = useState(false);

  // Fetch pricing config and freelancer data
  useEffect(() => {
    const fetchPricingData = async () => {
      try {
        // Fetch global config
        const configRes = await fetch(`${API_URL}/api/pricing/config`);
        if (configRes.ok) {
          const config = await configRes.json();
          setPricingConfig(config);
        }
        
        // Fetch freelancer pricing if team has an ID
        if (team.freelancer_id || team.id) {
          const freelancerId = team.freelancer_id || team.id;
          const freelancerRes = await fetch(`${API_URL}/api/pricing/freelancer/${freelancerId}`);
          if (freelancerRes.ok) {
            const data = await freelancerRes.json();
            setFreelancerPricing(data);
          }
        }
      } catch (error) {
        console.error('Error fetching pricing data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPricingData();
  }, [team]);

  // Calculate price whenever relevant values change
  useEffect(() => {
    if (step >= 3 && videoType && pricingConfig) {
      calculatePrice();
    }
  }, [step, videoType, rawMaterialHours, outputMinutes, videoCount, selectedAddons]);

  const calculatePrice = async () => {
    setCalculating(true);
    try {
      const freelancerId = team.freelancer_id || team.id;
      const response = await fetch(`${API_URL}/api/pricing/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          freelancer_id: freelancerId,
          video_type: videoType,
          raw_material_hours: rawMaterialHours,
          output_minutes: outputMinutes,
          video_count: videoCount,
          addons: selectedAddons
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        setCalculation(result);
      }
    } catch (error) {
      console.error('Error calculating price:', error);
    } finally {
      setCalculating(false);
    }
  };

  const handleContinue = () => {
    sessionStorage.setItem('revocut_calculation', JSON.stringify({
      team,
      teamId: team.id,
      videoType,
      rawMaterialHours,
      outputMinutes,
      videoCount,
      addons: selectedAddons,
      calculation
    }));
    navigate('/request');
  };

  const toggleAddon = (addonId) => {
    setSelectedAddons(prev => 
      prev.includes(addonId) 
        ? prev.filter(id => id !== addonId)
        : [...prev, addonId]
    );
  };

  const videoTypes = pricingConfig?.video_types || [];
  const addons = pricingConfig?.addons || [];

  // Video type icons mapping
  const getVideoTypeIcon = (icon) => {
    const icons = {
      'video': '🎬',
      'user': '👤',
      'car': '🚗',
      'gamepad': '🎮',
      'megaphone': '📢',
      'music': '🎵',
      'mic': '🎙️',
      'smartphone': '📱'
    };
    return icons[icon] || '🎥';
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.9)' }}>
        <div className="w-10 h-10 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.9)' }}>
      <div 
        className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-3xl border border-white/10"
        style={{ backgroundColor: '#111' }}
      >
        {/* Header */}
        <div className="sticky top-0 p-6 border-b border-white/10 flex items-center justify-between z-10" style={{ backgroundColor: '#111' }}>
          <div>
            <h2 className="text-xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>Projekt kalkulieren</h2>
            <p className="text-sm text-gray-500">{team.name} • {team.location?.city}</p>
          </div>
          <div className="flex items-center gap-4">
            {/* Step indicator */}
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4].map((s) => (
                <div 
                  key={s}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                    s === step 
                      ? 'bg-purple-500 text-white' 
                      : s < step 
                        ? 'bg-green-500 text-white'
                        : 'bg-white/10 text-gray-500'
                  }`}
                >
                  {s < step ? <Check className="w-4 h-4" /> : s}
                </div>
              ))}
            </div>
            <button onClick={onClose} className="p-2 rounded-xl hover:bg-white/10 transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Step 1: Video Type Selection */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Was für ein Video soll es werden?
                </h3>
                <p className="text-gray-400">Wähle den Videotyp aus – jeder Typ hat unterschiedliche Komplexität.</p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {videoTypes.map((vt) => (
                  <button
                    key={vt.id}
                    onClick={() => setVideoType(vt.id)}
                    className={`p-4 rounded-2xl border text-center transition-all ${
                      videoType === vt.id 
                        ? 'border-purple-500 bg-purple-500/20' 
                        : 'border-white/10 hover:border-white/30 bg-white/[0.02]'
                    }`}
                  >
                    <div className="text-3xl mb-2">{getVideoTypeIcon(vt.icon)}</div>
                    <div className="font-medium text-sm">{vt.name}</div>
                    <div className="text-xs text-gray-500 mt-1">×{vt.factor}</div>
                  </button>
                ))}
              </div>
              
              {videoType && (
                <div className="mt-6 p-4 rounded-xl bg-purple-500/10 border border-purple-500/30">
                  <p className="text-sm text-purple-300">
                    {videoTypes.find(vt => vt.id === videoType)?.description}
                  </p>
                </div>
              )}
              
              <button
                onClick={() => step === 1 && videoType && setStep(2)}
                disabled={!videoType}
                className={`w-full py-4 rounded-2xl font-semibold text-lg transition-all flex items-center justify-center gap-2 ${
                  videoType 
                    ? 'bg-purple-500 hover:scale-[1.02]' 
                    : 'bg-white/10 text-gray-500 cursor-not-allowed'
                }`}
              >
                Weiter
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Step 2: Video Details */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Projekt-Details
                </h3>
                <p className="text-gray-400">Wie viel Rohmaterial hast du und wie lang soll das Endvideo werden?</p>
              </div>
              
              <div className="space-y-6">
                {/* Raw Material */}
                <div className="p-6 rounded-2xl border border-white/10 bg-white/[0.02]">
                  <label className="block text-sm text-gray-400 mb-3">
                    Rohmaterial (Minuten)
                  </label>
                  <p className="text-xs text-gray-500 mb-4">
                    Wie viel Videomaterial hast du insgesamt aufgenommen?
                  </p>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="1"
                      max="600"
                      step="1"
                      value={rawMaterialHours * 60}
                      onChange={(e) => setRawMaterialHours(parseFloat(e.target.value) / 60)}
                      className="flex-1 accent-purple-500"
                    />
                    <div className="w-24 px-4 py-2 rounded-xl bg-white/10 text-center font-bold">
                      {rawMaterialHours >= 1 ? `${rawMaterialHours.toFixed(1)}h` : `${Math.round(rawMaterialHours * 60)} Min`}
                    </div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-2">
                    <span>1 Min</span>
                    <span>10+ Stunden</span>
                  </div>
                </div>

                {/* Output Length */}
                <div className="p-6 rounded-2xl border border-white/10 bg-white/[0.02]">
                  <label className="block text-sm text-gray-400 mb-3">
                    Gewünschte Videolänge (Sekunden/Minuten)
                  </label>
                  <p className="text-xs text-gray-500 mb-4">
                    Wie lang soll das fertige Video werden?
                  </p>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="15"
                      max="1800"
                      step="15"
                      value={outputMinutes * 60}
                      onChange={(e) => setOutputMinutes(parseFloat(e.target.value) / 60)}
                      className="flex-1 accent-purple-500"
                    />
                    <div className="w-24 px-4 py-2 rounded-xl bg-white/10 text-center font-bold">
                      {outputMinutes >= 1 ? `${outputMinutes.toFixed(1)} Min` : `${Math.round(outputMinutes * 60)} Sek`}
                    </div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-2">
                    <span>15 Sek</span>
                    <span>30 Min</span>
                  </div>
                </div>

                {/* Video Count */}
                <div className="p-6 rounded-2xl border border-white/10 bg-white/[0.02]">
                  <label className="block text-sm text-gray-400 mb-3">
                    Anzahl Videos
                  </label>
                  <div className="flex items-center gap-3">
                    <button 
                      onClick={() => setVideoCount(Math.max(1, videoCount - 1))} 
                      className="w-12 h-12 rounded-xl border border-white/20 hover:border-purple-500 transition-colors text-xl font-bold"
                    >
                      -
                    </button>
                    <input 
                      type="number" 
                      value={videoCount} 
                      onChange={(e) => setVideoCount(Math.max(1, parseInt(e.target.value) || 1))} 
                      className="w-20 h-12 rounded-xl bg-white/10 border border-white/10 text-center text-xl font-bold" 
                      min="1" 
                    />
                    <button 
                      onClick={() => setVideoCount(videoCount + 1)} 
                      className="w-12 h-12 rounded-xl border border-white/20 hover:border-purple-500 transition-colors text-xl font-bold"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 py-4 rounded-2xl font-semibold border border-white/20 hover:border-white/40 transition-all"
                >
                  Zurück
                </button>
                <button
                  onClick={() => setStep(3)}
                  className="flex-1 py-4 rounded-2xl font-semibold bg-purple-500 hover:scale-[1.02] transition-all flex items-center justify-center gap-2"
                >
                  Weiter
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Addons */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Zusatzleistungen
                </h3>
                <p className="text-gray-400">Wähle optionale Extras für dein Video aus.</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {addons.map((addon) => (
                  <div
                    key={addon.id}
                    onClick={() => toggleAddon(addon.id)}
                    className={`p-4 rounded-2xl cursor-pointer transition-all border ${
                      selectedAddons.includes(addon.id) 
                        ? 'border-purple-500 bg-purple-500/10' 
                        : 'border-white/10 hover:border-white/20'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium">{addon.name}</span>
                        <p className="text-xs text-gray-500 mt-1">{addon.description}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-purple-400 font-medium">
                          +{addon.percentage}%
                        </span>
                        <div className={`w-6 h-6 rounded flex items-center justify-center ${
                          selectedAddons.includes(addon.id) ? 'bg-purple-500' : 'border border-white/30'
                        }`}>
                          {selectedAddons.includes(addon.id) && <Check className="w-4 h-4" />}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setStep(2)}
                  className="flex-1 py-4 rounded-2xl font-semibold border border-white/20 hover:border-white/40 transition-all"
                >
                  Zurück
                </button>
                <button
                  onClick={() => setStep(4)}
                  className="flex-1 py-4 rounded-2xl font-semibold bg-purple-500 hover:scale-[1.02] transition-all flex items-center justify-center gap-2"
                >
                  Zur Zusammenfassung
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Summary */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Zusammenfassung
                </h3>
                <p className="text-gray-400">Überprüfe deine Kalkulation</p>
              </div>
              
              {calculating ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : calculation ? (
                <div className="space-y-4">
                  {/* Calculation Breakdown */}
                  <div className="p-6 rounded-2xl border border-white/10 bg-white/[0.02] space-y-4">
                    <h4 className="font-semibold text-gray-300 mb-4">Detaillierte Kalkulation</h4>
                    
                    <div className="flex justify-between items-center py-2 border-b border-white/5">
                      <span className="text-gray-400">Basispreis pro Minute</span>
                      <span>€{calculation.base_price_per_minute}</span>
                    </div>
                    
                    <div className="flex justify-between items-center py-2 border-b border-white/5">
                      <span className="text-gray-400">Videotyp: {calculation.video_type}</span>
                      <span className="text-purple-400">×{calculation.video_type_factor}</span>
                    </div>
                    
                    <div className="flex justify-between items-center py-2 border-b border-white/5">
                      <span className="text-gray-400">Rohmaterial ({rawMaterialHours >= 1 ? `${rawMaterialHours.toFixed(1)}h` : `${Math.round(rawMaterialHours * 60)} Min`})</span>
                      <span className="text-purple-400">×{calculation.raw_material_factor}</span>
                    </div>
                    
                    <div className="flex justify-between items-center py-2 border-b border-white/5">
                      <span className="text-gray-400">Videolänge ({outputMinutes >= 1 ? `${outputMinutes.toFixed(1)} Min` : `${Math.round(outputMinutes * 60)} Sek`})</span>
                      <span className="text-purple-400">×{calculation.output_length_factor}</span>
                    </div>
                    
                    <div className="flex justify-between items-center py-2 border-b border-white/5">
                      <span className="text-gray-400">Zwischensumme pro Video</span>
                      <span>€{calculation.subtotal_per_video}</span>
                    </div>
                    
                    {Object.keys(calculation.addon_breakdown).length > 0 && (
                      <div className="pt-2">
                        <p className="text-sm text-gray-500 mb-2">Add-ons (+{calculation.addon_total_percentage}%)</p>
                        {Object.entries(calculation.addon_breakdown).map(([name, cost]) => (
                          <div key={name} className="flex justify-between items-center py-1 text-sm">
                            <span className="text-gray-500">{name}</span>
                            <span className="text-green-400">+€{cost}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <div className="flex justify-between items-center py-2 border-b border-white/5">
                      <span className="text-gray-400">Preis pro Video</span>
                      <span className="font-medium">€{calculation.price_per_video}</span>
                    </div>
                    
                    <div className="flex justify-between items-center py-2 border-b border-white/5">
                      <span className="text-gray-400">Anzahl Videos</span>
                      <span>×{calculation.video_count}</span>
                    </div>
                  </div>
                  
                  {/* Total */}
                  <div className="p-6 rounded-2xl border border-purple-500/30 bg-purple-500/10">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold">Gesamtpreis</span>
                      <span className="text-3xl font-bold" style={{ color: '#5C63FF' }}>
                        €{calculation.total_price.toFixed(2)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Info Box */}
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <p className="text-xs text-gray-500">
                      * Dies ist eine unverbindliche Schätzung. Der finale Preis kann nach Sichtung des Materials variieren.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  Fehler bei der Berechnung
                </div>
              )}
              
              <div className="flex gap-3">
                <button
                  onClick={() => setStep(3)}
                  className="flex-1 py-4 rounded-2xl font-semibold border border-white/20 hover:border-white/40 transition-all"
                >
                  Zurück
                </button>
                <button
                  onClick={handleContinue}
                  disabled={!calculation}
                  className="flex-1 py-4 rounded-2xl font-semibold bg-purple-500 hover:scale-[1.02] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anfrage senden
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Freelancer Application Modal */}
      <FreelancerApplicationModal 
        isOpen={showApplicationModal} 
        onClose={() => setShowApplicationModal(false)} 
      />
    </div>
  );
};

export default Home;
